import { api } from "./client";
import { unwrapApiList } from "./unwrap";
import { ApiClientError, type ApiErrorBody } from "./types";

/** org_verticals.vertical_code — matches backend. */
export const VerticalCode = {
  TRADING: 0,
  MANUFACTURING: 1,
  SERVICES: 2,
} as const;

export type VerticalCodeValue = (typeof VerticalCode)[keyof typeof VerticalCode];

export interface OrgSettings {
  org_id: string;
  org_name: string;
  base_currency: string;
  vertical_code: VerticalCodeValue;
  tax_registration_number: string | null;
}

export interface OrgBranch {
  branchId: string;
  branchCode: string;
  nameI18nKey: string;
  status: number;
}

export interface OrgMember {
  userId: string;
  displayName: string;
  email: string;
  status: number;
}

export interface StandardTemplateRestoreResult {
  accountsSeeded: number;
  defaultsSeeded: number;
}

export async function fetchOrgSettings(): Promise<OrgSettings> {
  const res = await api.get<{ ok: true; org: OrgSettings }>("/v1/settings/org");
  if (!res.ok || !("org" in res)) {
    throw new Error("Failed to load organization settings");
  }
  return res.org;
}

export async function fetchBranches(): Promise<OrgBranch[]> {
  const res = await api.get<{ ok: true; data: OrgBranch[] }>("/v1/org/branches");
  return unwrapApiList(res);
}

export async function fetchOrgMembers(): Promise<OrgMember[]> {
  const res = await api.get<{ ok: true; data: OrgMember[] }>("/v1/org/members");
  return unwrapApiList(res);
}

/** Restores V3/V4 standard COA template + defaults for legacy orgs. SUPER_ADMIN only. */
export interface EnsureFinanceBooksResult {
  defaultsBound: number;
  missingAccountCodes: string[];
}

export interface ChequeBackfillResult {
  arInserted: number;
  apInserted: number;
  arSkipped: number;
  apSkipped: number;
  errors: string[];
}

export async function backfillFinanceCheques(): Promise<ChequeBackfillResult> {
  const res = await api.post<
    | { ok: true; data: ChequeBackfillResult; message?: string }
    | ApiErrorBody
  >("/v1/org/finance-cheques/backfill", {});
  if (!("ok" in res) || res.ok !== true) {
    const err = res as ApiErrorBody;
    const e = err.error;
    throw new ApiClientError(
      e?.message || "فشل استيراد الشيكات القديمة",
      e?.code || 500,
      e?.code || 500,
      e?.reason,
    );
  }
  return res.data;
}

export async function ensureFinanceBooks(): Promise<EnsureFinanceBooksResult> {
  const res = await api.post<
    | { ok: true; data: EnsureFinanceBooksResult }
    | ApiErrorBody
  >("/v1/org/finance-books/ensure", {});
  if (!("ok" in res) || res.ok !== true) {
    const err = res as ApiErrorBody;
    const e = err.error;
    throw new ApiClientError(
      e?.message || "فشل تجهيز حسابات الدفاتر",
      e?.code || 500,
      e?.code || 500,
      e?.reason,
    );
  }
  return res.data;
}

export async function restoreStandardOrgTemplate(): Promise<StandardTemplateRestoreResult> {
  const res = await api.post<
    | { ok: true; data: StandardTemplateRestoreResult }
    | ApiErrorBody
  >("/v1/org/template/restore", {});
  if (!("ok" in res) || res.ok !== true) {
    const err = res as ApiErrorBody;
    const e = err.error;
    throw new ApiClientError(
      e?.message || "فشلت عملية إعادة تهيئة الدليل المحاسبي",
      e?.code || 500,
      e?.code || 500,
      e?.reason,
    );
  }
  return res.data;
}
